/**
 * Common API utilities for authenticated fetch requests in the limited cases when we don't use tRPC
 * Intent: Centralize REST API calls with consistent authentication and error handling
 * Used for file uploads/downloads and operations not suitable for tRPC
 */
// Import backend-defined response types for type safety
import type {
  DeleteDocumentResponse,
  EntityInput,
  UploadDocumentResponse,
  UploadProposalDocumentResponse,
} from '@teo/backend';
import { type AgentAttachment, agentAttachmentUploadResponseSchema } from '@teo/types';

/**
 * Response from the document access token endpoint
 */
interface DocumentAccessTokenResponse {
  accessToken: string;
  expiresIn: number;
  viewUrl: string;
}

/**
 * Get a short-lived access token for viewing a document
 * This is more secure than putting the full JWT in the URL
 * @param documentId - Document display ID
 * @returns Promise with access token response or null if failed
 */
export async function getDocumentAccessToken(
  documentId: string,
): Promise<DocumentAccessTokenResponse | null> {
  try {
    const response = await authenticatedFetch(`/api/documents/${documentId}/access-token`, {
      method: 'POST',
    });
    if (!response.ok) {
      console.error('Failed to get document access token:', response.status);
      return null;
    }
    return (await response.json()) as DocumentAccessTokenResponse;
  } catch (error) {
    console.error('Error getting document access token:', error);
    return null;
  }
}

interface OpenDocumentOptions {
  download?: boolean;
}

/**
 * Open a document in a new tab using a secure access token
 * @param documentId - Document display ID
 * @param options - Optional behavior flags, such as forcing a download response
 */
export async function openDocument(
  documentId: string,
  options: OpenDocumentOptions = {},
): Promise<void> {
  const tokenResponse = await getDocumentAccessToken(documentId);
  if (tokenResponse) {
    const separator = tokenResponse.viewUrl.includes('?') ? '&' : '?';
    const targetUrl = options.download
      ? `${tokenResponse.viewUrl}${separator}download=1`
      : tokenResponse.viewUrl;
    if (options.download) {
      window.location.href = targetUrl;
    } else {
      window.open(targetUrl, '_blank');
    }
  } else {
    console.error('Failed to get document access token');
  }
}

/**
 * Authenticated fetch wrapper that automatically includes auth headers
 * @param url - API endpoint URL
 * @param options - Fetch options (headers will be merged with auth headers)
 * @returns Promise<Response>
 */
export async function authenticatedFetch(
  url: string,
  options: RequestInit = {},
): Promise<Response> {
  const token = localStorage.getItem('authToken');

  const headers = {
    ...options.headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  return fetch(url, {
    ...options,
    headers,
  });
}

/**
 * Safe API call wrapper that returns backend-typed response
 * @param url - API endpoint URL
 * @param options - Fetch options
 * @returns Promise with backend-typed response
 */
async function safeApiCall<T>(url: string, options: RequestInit = {}): Promise<T> {
  try {
    const response = await authenticatedFetch(url, options);
    return (await response.json()) as T;
  } catch (error) {
    // Backend always returns the discriminant union structure, even for errors
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error',
    } as T;
  }
}

/**
 * Upload a file to a specific endpoint with authentication
 * @param url - Upload endpoint URL
 * @param file - File to upload
 * @param additionalData - Additional form data fields
 * @returns Promise with backend-typed upload response
 */
export async function uploadFile<T>(
  url: string,
  file: File,
  additionalData?: Record<string, string>,
): Promise<T> {
  const formData = new FormData();
  formData.append('file', file);

  if (additionalData) {
    Object.entries(additionalData).forEach(([key, value]) => {
      formData.append(key, value);
    });
  }

  return safeApiCall<T>(url, {
    method: 'POST',
    body: formData,
  });
}

/**
 * Delete a resource with authentication
 * @param url - Delete endpoint URL
 * @returns Promise with backend-typed delete response
 */
export async function deleteResource<T>(url: string): Promise<T> {
  return safeApiCall<T>(url, {
    method: 'DELETE',
  });
}

export async function uploadEntityDocument(
  entityReference: EntityInput,
  file: File,
): Promise<UploadDocumentResponse> {
  switch (entityReference.type) {
    case 'ownership':
      return uploadFile<UploadDocumentResponse>(
        `/api/ownership/${entityReference.ownershipId}/documents`,
        file,
      );
    case 'portfolio':
      return uploadFile<UploadDocumentResponse>(
        `/api/portfolio/${entityReference.portfolioId}/documents`,
        file,
      );
    case 'property':
      return uploadFile<UploadDocumentResponse>(
        `/api/property/${entityReference.propertyId}/documents`,
        file,
      );
  }
}

export async function uploadContractStatusDocument(
  contractStatusId: string,
  entityInput: EntityInput,
  file: File,
): Promise<UploadDocumentResponse> {
  return uploadFile<UploadDocumentResponse>(
    `/api/contract/${contractStatusId}/documents`,
    file,
    entityInput as Record<string, string>,
  );
}

/**
 * Upload a vendor's proposal PDF against an RFP. Besides storing the
 * document, the server auto-enqueues vendor-name + RFP-match extraction;
 * the response carries the task displayId the caller polls via
 * proposals.getVendorNameOutput.
 */
export async function uploadRfpProposalDocument(
  rfpDisplayId: string,
  file: File,
): Promise<UploadProposalDocumentResponse> {
  return uploadFile<UploadProposalDocumentResponse>(
    `/api/rfp/${rfpDisplayId}/proposal-documents`,
    file,
  );
}

export async function uploadInvoiceDocument(
  invoiceStatusId: string,
  entityInput: EntityInput,
  file: File,
): Promise<UploadDocumentResponse> {
  return uploadFile<UploadDocumentResponse>(
    `/api/invoice/${invoiceStatusId}/documents`,
    file,
    entityInput as Record<string, string>,
  );
}

/**
 * Attach a file to the next question in the agent panel. The file stays the
 * person's own until the agent files it where the person says.
 */
export async function uploadAgentAttachment(file: File): Promise<AgentAttachment> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await authenticatedFetch('/api/agent/attachments', {
    method: 'POST',
    body: formData,
  });
  const parsed = agentAttachmentUploadResponseSchema.safeParse(await response.json());
  if (!parsed.success) throw new Error(`${file.name} could not be attached.`);
  if (!parsed.data.success) throw new Error(parsed.data.error);
  return parsed.data.attachment;
}

export async function deleteEntityDocument(
  entityReference: EntityInput,
  documentId: string,
): Promise<DeleteDocumentResponse> {
  switch (entityReference.type) {
    case 'ownership':
      return deleteResource<DeleteDocumentResponse>(
        `/api/ownership/${entityReference.ownershipId}/documents/${documentId}`,
      );
    case 'portfolio':
      return deleteResource<DeleteDocumentResponse>(
        `/api/portfolio/${entityReference.portfolioId}/documents/${documentId}`,
      );
    case 'property':
      return deleteResource<DeleteDocumentResponse>(
        `/api/property/${entityReference.propertyId}/documents/${documentId}`,
      );
  }
}
